import type {
  AgentSessionStatus,
  AlertInstanceState,
  ContainerState,
  DeploymentStatus,
  DomainStatus,
  HealthStatus,
  HostState,
  ImportConfidence,
  ImportConflictKind,
  InstanceStatus,
  LicenseState,
  NodeStatus,
  PlacementStatus,
} from "../api/types";
import "./StatePill.css";

export type PillTone = "positive" | "neutral" | "warning" | "info" | "negative";
export type PillShape = "dot" | "ring" | "bars" | "spin" | "triangle";

interface StatePillProps {
  label: string;
  tone: PillTone;
  shape: PillShape;
}

/** Small inline glyphs so status reads by shape as well as color — an
 * operator with color-vision deficiency (or a black-and-white terminal
 * screenshot) can still tell "running" from "exited" from "paused." */
function Glyph({ shape }: { shape: PillShape }) {
  switch (shape) {
    case "dot":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <circle cx="4" cy="4" r="4" fill="currentColor" />
        </svg>
      );
    case "ring":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <circle cx="4" cy="4" r="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case "bars":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <rect x="1" y="0" width="2" height="8" fill="currentColor" />
          <rect x="5" y="0" width="2" height="8" fill="currentColor" />
        </svg>
      );
    case "spin":
      return (
        <svg
          viewBox="0 0 8 8"
          width="8"
          height="8"
          aria-hidden="true"
          focusable="false"
          className="state-pill__spin"
        >
          <path d="M4 0a4 4 0 1 1-3.5 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "triangle":
      return (
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden="true" focusable="false">
          <path d="M4 0 L8 8 L0 8 Z" fill="currentColor" />
        </svg>
      );
  }
}

export function StatePill({ label, tone, shape }: StatePillProps) {
  return (
    <span className={`state-pill state-pill--${tone}`}>
      <Glyph shape={shape} />
      {label}
    </span>
  );
}

const CONTAINER_STATE_META: Record<ContainerState, { label: string; tone: PillTone; shape: PillShape }> = {
  running: { label: "Running", tone: "positive", shape: "dot" },
  exited: { label: "Exited", tone: "neutral", shape: "ring" },
  paused: { label: "Paused", tone: "warning", shape: "bars" },
  restarting: { label: "Restarting", tone: "info", shape: "spin" },
  created: { label: "Created", tone: "neutral", shape: "ring" },
  dead: { label: "Dead", tone: "negative", shape: "triangle" },
};

export function ContainerStatePill({ state }: { state: ContainerState }) {
  const meta = CONTAINER_STATE_META[state];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/** Whether a container is Comebye-managed (`Container.managed`, ADR-0038) — an
 * unmanaged container is adoptable. Deliberately neutral tone for "Adoptable," not a
 * warning: an unmanaged container is an ordinary, expected state for a brownfield host,
 * not a problem to flag. */
export function ContainerManagedPill({ managed }: { managed: boolean }) {
  return managed ? (
    <StatePill label="Managed" tone="positive" shape="dot" />
  ) : (
    <StatePill label="Adoptable" tone="neutral" shape="ring" />
  );
}

/**
 * A node's classification badge (`Node.nodeType`, migration 0058 / ADR-0034).
 * Shown ONLY for a `local` node — a developer's own machine, which a
 * production-class environment can never hold (the ADR-0034/0035 admission
 * invariant). A `standard` node (the schema default, the overwhelming common
 * case) gets no badge so the list stays uncluttered: absence reads as standard.
 * Info tone, not warning — a local node is a legitimate, expected on-ramp state,
 * not a problem to flag. An unknown type (version skew, A-3) renders nothing
 * rather than guessing at meaning.
 */
export function NodeTypeBadge({ nodeType }: { nodeType: string }) {
  if (nodeType !== "local") return null;
  return <StatePill label="Local" tone="info" shape="ring" />;
}

const NODE_STATUS_META: Record<NodeStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  online: { label: "Online", tone: "positive", shape: "dot" },
  offline: { label: "Offline", tone: "negative", shape: "triangle" },
  unknown: { label: "Unknown", tone: "neutral", shape: "ring" },
};

export function NodeStatusPill({ status }: { status: NodeStatus }) {
  const meta = NODE_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * `Host.liveAgent.status` (host/deploy UX review, #526) mirrors `Node.status`
 * but is typed as a plain `string` on the wire — a version-skew-safe read
 * model, not the closed `NodeStatus` union. Reuses `NODE_STATUS_META` for a
 * recognized value; an unrecognized one (A-3: skew is normal) renders the
 * server's own string in a neutral pill rather than guessing or crashing.
 */
export function HostAgentStatusPill({ status }: { status: string }) {
  const meta =
    status === "online" || status === "offline" || status === "unknown"
      ? NODE_STATUS_META[status]
      : { label: status, tone: "neutral" as const, shape: "ring" as const };
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * A Host's unified lifecycle (host/deploy UX review, #526 — see `HostState`'s
 * doc comment in types.ts, and `server/internal/hostsvc.deriveState` for the
 * exact precedence this mirrors). `provisioning` reads as an active,
 * Comebye-side in-flight action (info/spin); `enrolling` is a quieter wait —
 * the provider says the machine is up, but no agent has enrolled yet
 * ("running ≠ enrolled") — never something Comebye itself is doing
 * (neutral/ring). `provision_failed` and `offline` both read as trouble
 * (negative/triangle) but are never conflated in copy — the label says
 * which; note `offline` also covers a node whose live status is "unknown"
 * (never confirmed contact), matching the server's collapse of that case.
 * `decommissioning` mirrors `InstanceStatus.deleting`'s warning/bars;
 * `deleted` is terminal and inert, same neutral/ring as
 * `InstanceStatus.deleted`.
 */
const HOST_STATE_META: Record<HostState, { label: string; tone: PillTone; shape: PillShape }> = {
  provisioning: { label: "Provisioning", tone: "info", shape: "spin" },
  enrolling: { label: "Enrolling", tone: "neutral", shape: "ring" },
  online: { label: "Online", tone: "positive", shape: "dot" },
  offline: { label: "Offline", tone: "negative", shape: "triangle" },
  provision_failed: { label: "Provisioning failed", tone: "negative", shape: "triangle" },
  decommissioning: { label: "Decommissioning", tone: "warning", shape: "bars" },
  deleted: { label: "Deleted", tone: "neutral", shape: "ring" },
};

export function HostStatePill({ state }: { state: HostState }) {
  const meta = HOST_STATE_META[state];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// `Service.status` is server-derived infrastructure state, not a fixed
// client-known enum (see the doc comment on `Service` in types.ts) — today
// it is only ever `"created"` (deploying isn't implemented). Known values
// get real styling; anything else falls back to a plain neutral pill
// showing the server's own string rather than guessing at meaning.
const SERVICE_STATUS_META: Record<string, { label: string; tone: PillTone; shape: PillShape }> = {
  created: { label: "Created", tone: "neutral", shape: "ring" },
  deploying: { label: "Deploying", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  stopped: { label: "Stopped", tone: "neutral", shape: "ring" },
  error: { label: "Error", tone: "negative", shape: "triangle" },
};

export function ServiceStatusPill({ status }: { status: string }) {
  const meta = SERVICE_STATUS_META[status] ?? { label: status, tone: "neutral" as const, shape: "ring" as const };
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

const DEPLOYMENT_STATUS_META: Record<DeploymentStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  pending: { label: "Pending", tone: "neutral", shape: "ring" },
  provisioning: { label: "Provisioning", tone: "info", shape: "spin" },
  deploying: { label: "Deploying", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  failed: { label: "Failed", tone: "negative", shape: "triangle" },
  rolled_back: { label: "Rolled back", tone: "warning", shape: "bars" },
  rollback_failed: { label: "Rollback failed", tone: "negative", shape: "triangle" },
};

export function DeploymentStatusPill({ status }: { status: DeploymentStatus }) {
  const meta = DEPLOYMENT_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * One placement's outcome status within a deployment's per-placement
 * convergence breakdown (Host/Deploy UX Phase 2, #539 —
 * `DeploymentPlacementOutcome.status`). Mirrors the server's placement outcome
 * vocabulary (chiefly running/failed/rolled_back) but is typed as a plain
 * `string` on the wire, not a closed union (A-3: version skew is normal) — a
 * recognized value reuses the SAME meta as the deployment-level pill so the
 * two read consistently; an unrecognized one renders the server's own string
 * in a neutral pill rather than guessing or crashing.
 */
export function DeploymentPlacementStatusPill({ status }: { status: string }) {
  const meta =
    status in DEPLOYMENT_STATUS_META
      ? DEPLOYMENT_STATUS_META[status as DeploymentStatus]
      : { label: status, tone: "neutral" as const, shape: "ring" as const };
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

const INSTANCE_STATUS_META: Record<InstanceStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  requested: { label: "Requested", tone: "neutral", shape: "ring" },
  provisioning: { label: "Provisioning", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  deleting: { label: "Deleting", tone: "warning", shape: "bars" },
  // Not "neutral": the host is still running and still being billed, and it stays
  // that way until the operator confirms the termination at the provider.
  deprovision_pending: { label: "Awaiting provider confirmation", tone: "warning", shape: "triangle" },
  deleted: { label: "Deleted", tone: "neutral", shape: "ring" },
  error: { label: "Error", tone: "negative", shape: "triangle" },
};

export function InstanceStatusPill({ status }: { status: InstanceStatus }) {
  const meta = INSTANCE_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// A placement's lifecycle is entirely server-owned (S-15, ADR-0025 §2) — this
// pill only renders it, it never infers or advances it.
const PLACEMENT_STATUS_META: Record<PlacementStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  pending: { label: "Pending", tone: "neutral", shape: "ring" },
  deploying: { label: "Deploying", tone: "info", shape: "spin" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  failed: { label: "Failed", tone: "negative", shape: "triangle" },
};

export function PlacementStatusPill({ status }: { status: PlacementStatus }) {
  const meta = PLACEMENT_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// A bound domain's edge routing/cert state (#303) is server-observed from the
// node's live route table + autocert cache — this pill only renders it. The empty
// string is the honest "unknown" (no node has reported yet), rendered as a muted
// ring rather than a fabricated "active" (S-15). "pending" is the TTFU moment the
// Founder is watching for — the cert is issuing — so it reads as an in-progress
// spinner, not an error.
const DOMAIN_STATUS_META: Record<Exclude<DomainStatus, "">, { label: string; tone: PillTone; shape: PillShape }> = {
  active: { label: "Active", tone: "positive", shape: "dot" },
  pending: { label: "Issuing certificate…", tone: "info", shape: "spin" },
  disabled: { label: "HTTP only", tone: "neutral", shape: "ring" },
};

export function DomainStatusPill({ status }: { status?: DomainStatus }) {
  if (!status) {
    return <StatePill label="Awaiting status" tone="neutral" shape="ring" />;
  }
  const meta = DOMAIN_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/** Whether a connection's two endpoints currently share a node
 * (ADR-0029 §2) — computed client-side from placements (see
 * `ConnectionsSection`'s `useConnectionTopology`), never server-asserted.
 * `"unplaced"` means at least one endpoint has no placement yet, so
 * same-node vs cross-node isn't a real fact to state (S-15) — this is
 * distinct from "unknown reachability", which is about a PLACED cross-node
 * pair's live dial health. */
export type ConnectionTopology = "same-node" | "cross-node" | "unplaced";

const CONNECTION_TOPOLOGY_META: Record<ConnectionTopology, { label: string; tone: PillTone; shape: PillShape }> = {
  "same-node": { label: "Same machine", tone: "neutral", shape: "ring" },
  "cross-node": { label: "Cross-machine", tone: "info", shape: "bars" },
  unplaced: { label: "Not placed", tone: "neutral", shape: "ring" },
};

export function ConnectionTopologyPill({ topology }: { topology: ConnectionTopology }) {
  const meta = CONNECTION_TOPOLOGY_META[topology];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * Live reachability (ADR-0029 P3-5/P3-6 — see `ConnectionStatus`'s doc
 * comment in types.ts). `undefined` covers BOTH "the check hasn't loaded
 * yet" and "the endpoint isn't available" (P3-5 unmerged) — both render the
 * same neutral "Unknown" pill as the reachable:"unknown" wire value, never
 * an alarm and never a fabricated positive/negative (S-15).
 */
const CONNECTION_REACHABLE_META: Record<"yes" | "no" | "unknown", { label: string; tone: PillTone; shape: PillShape }> = {
  yes: { label: "Reachable", tone: "positive", shape: "dot" },
  no: { label: "Unreachable", tone: "negative", shape: "triangle" },
  unknown: { label: "Unknown", tone: "neutral", shape: "ring" },
};

export function ConnectionReachabilityPill({ reachable }: { reachable: "yes" | "no" | "unknown" | undefined }) {
  const meta = CONNECTION_REACHABLE_META[reachable ?? "unknown"];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// `stale`/`absent` are deliberately "neutral" tone, not "warning" — neither
// is alarming on its own (see entitlementRules.ts for the fuller rationale:
// `stale` means still-fully-licensed, `absent` is the ordinary self-hosted
// day-one state). `expired`/`invalid` ARE meant to read as trouble.
const LICENSE_STATE_META: Record<LicenseState, { label: string; tone: PillTone; shape: PillShape }> = {
  active: { label: "Active", tone: "positive", shape: "dot" },
  stale: { label: "Stale (still licensed)", tone: "neutral", shape: "ring" },
  expired: { label: "Expired", tone: "warning", shape: "triangle" },
  absent: { label: "No license", tone: "neutral", shape: "ring" },
  invalid: { label: "Invalid", tone: "negative", shape: "triangle" },
};

export function LicenseStatePill({ state }: { state: LicenseState }) {
  const meta = LICENSE_STATE_META[state];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// The fleet health matrix's per-cell rollup (ADR-0030 P4-2 — mirrors
// `projectsvc.HealthStatus`/`rollupHealth`'s precedence exactly, see
// types.ts). "unreachable" outranks everything else (a server that lost its
// tunnel can't be trusted to still be running what it last reported), which
// is why it reads as more severe (negative/triangle) than "failed" itself
// would suggest in isolation — this mirrors the server's own precedence, not
// an independent UI judgment call.
const HEALTH_STATUS_META: Record<HealthStatus, { label: string; tone: PillTone; shape: PillShape }> =
  {
    running: { label: "Running", tone: "positive", shape: "dot" },
    unhealthy: { label: "Unhealthy", tone: "negative", shape: "triangle" },
    unreachable: { label: "Unreachable", tone: "negative", shape: "triangle" },
    pending: { label: "Pending", tone: "neutral", shape: "ring" },
    failed: { label: "Failed", tone: "negative", shape: "triangle" },
    unknown: { label: "Unknown", tone: "neutral", shape: "ring" },
  };

export function HealthStatePill({ health }: { health: HealthStatus }) {
  const meta = HEALTH_STATUS_META[health];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// An alert instance's lifecycle (ADR-0030 P4-5 — mirrors
// `alertsvc.AlertState*` exactly, see types.ts's AlertInstanceState doc
// comment). "resolved" is deliberately neutral, not positive — it is a past
// occurrence, not a current all-clear signal about the rule overall.
const ALERT_INSTANCE_STATE_META: Record<
  AlertInstanceState,
  { label: string; tone: PillTone; shape: PillShape }
> = {
  pending: { label: "Pending", tone: "neutral", shape: "ring" },
  firing: { label: "Firing", tone: "negative", shape: "triangle" },
  resolved: { label: "Resolved", tone: "neutral", shape: "dot" },
};

export function AlertInstanceStatePill({ state }: { state: AlertInstanceState }) {
  const meta = ALERT_INSTANCE_STATE_META[state];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// Org import (ADR-0048 §2, #472) — how sure the server's inference is that a
// proposed mapping is correct (see ProposedImportItem's doc comment in
// types.ts). "low" reads as warning, not negative: it isn't wrong, it just
// needs a human, same posture as an untagged/foreign-scheme conflict below.
const IMPORT_CONFIDENCE_META: Record<ImportConfidence, { label: string; tone: PillTone; shape: PillShape }> = {
  high: { label: "High confidence", tone: "positive", shape: "dot" },
  medium: { label: "Medium confidence", tone: "info", shape: "ring" },
  low: { label: "Needs review", tone: "warning", shape: "triangle" },
};

export function ImportConfidencePill({ confidence }: { confidence: ImportConfidence }) {
  const meta = IMPORT_CONFIDENCE_META[confidence];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// Org import conflict kinds (ADR-0048 §2) — mirrors orgmap.ConflictKind. Only
// "foreign-tenant" is negative/blocking: it is the one conflict that can never be
// committed (S-16/S-17); "untagged"/"foreign-scheme" just mean a human has to place
// the resource, not that anything is wrong.
const IMPORT_CONFLICT_META: Record<ImportConflictKind, { label: string; tone: PillTone; shape: PillShape }> = {
  untagged: { label: "No labels or tags", tone: "neutral", shape: "ring" },
  "foreign-scheme": { label: "Organized by another tool", tone: "warning", shape: "bars" },
  "foreign-tenant": { label: "Belongs to another tenant", tone: "negative", shape: "triangle" },
};

export function ImportConflictPill({ kind }: { kind: ImportConflictKind }) {
  const meta = IMPORT_CONFLICT_META[kind];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * A project's corpus binding + sync lifecycle (ADR-0052/ADR-0053 §3 — see `CorpusBinding`'s
 * doc comment in types.ts). Computed CLIENT-SIDE from two independent reads
 * (`CorpusBinding` presence, `CorpusList.sourceCommit`), never a server-asserted field — so
 * there is nothing to keep in sync (A-5), and CorpusPage/CorpusHealthSummary derive it fresh
 * on every render rather than caching it anywhere. "unbound" and "pending" are both
 * deliberately neutral/info, never negative — neither is a problem to flag, they're the
 * ordinary early states of describing a new app (A-10: never render an alarm for an honest
 * absence).
 */
export type CorpusBindingStatus = "unbound" | "pending" | "synced";

const CORPUS_BINDING_STATUS_META: Record<CorpusBindingStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  unbound: { label: "Not bound", tone: "neutral", shape: "ring" },
  pending: { label: "Awaiting first sync", tone: "info", shape: "spin" },
  synced: { label: "Synced", tone: "positive", shape: "dot" },
};

export function CorpusBindingStatusPill({ status }: { status: CorpusBindingStatus }) {
  const meta = CORPUS_BINDING_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * The provenance rung a project's CURRENTLY-SYNCED corpus actually earned (`ProvenanceRung`
 * in types.ts — ADR-0053 §2's trust ladder). Only render this for a corpus that has ACTUALLY
 * synced (`CorpusList.sourceCommit` non-empty) — a binding's `expectedRung` before any sync
 * has happened is a REQUIREMENT, not an earned badge, and showing it here would imply a
 * verification that hasn't occurred yet (A-10; render the requirement as plain text instead,
 * see CorpusHealthSummary).
 *
 * `repo-watch` reads distinctly WEAKER (info tone) than `ci-attested`/`signed-commit`
 * (positive tone) — never all three painted the same "verified" green — because it is the
 * one rung earned without a CI attestation or a commit signature (proto.corpusprov.go: "byte-
 * faithful AND GitHub-App-attested, but NOT CI-attested"). An unrecognized future rung (A-3)
 * renders the server's own string in a neutral pill rather than guessing at its strength.
 */
const PROVENANCE_RUNG_META: Record<string, { label: string; tone: PillTone; shape: PillShape }> = {
  "repo-watch": { label: "Repo-watch", tone: "info", shape: "ring" },
  "ci-attested": { label: "CI-attested", tone: "positive", shape: "dot" },
  "signed-commit": { label: "Signed commit", tone: "positive", shape: "dot" },
};

export function ProvenanceRungPill({ rung }: { rung: string }) {
  const meta = PROVENANCE_RUNG_META[rung] ?? { label: rung, tone: "neutral" as const, shape: "ring" as const };
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * A governed-promotion decision's verdict (ADR-0058, `GovernanceVerdict` in
 * `features/governance/governanceDecision.ts`) — the Governance Decisions view's
 * headline signal. "deny" is deliberately the LOUDEST pill this file defines
 * (negative/triangle, same shape as a confirmed audit-chain break) because a denied
 * promotion is the exact "did my declared governance bite" moment a Platform Lead is
 * scanning for (A-10: it must never read as just another neutral row). "allow" is
 * positive/dot, matching every other "this control admitted the action" pill in this
 * file. "unknown" (a row whose `outcome` this action never actually writes — version
 * skew) renders neutral/ring rather than guessing either direction.
 */
const GOVERNANCE_VERDICT_META: Record<"allow" | "deny" | "unknown", { label: string; tone: PillTone; shape: PillShape }> = {
  allow: { label: "Allowed", tone: "positive", shape: "dot" },
  deny: { label: "Denied", tone: "negative", shape: "triangle" },
  unknown: { label: "Unknown", tone: "neutral", shape: "ring" },
};

export function GovernanceVerdictPill({ verdict }: { verdict: "allow" | "deny" | "unknown" }) {
  const meta = GOVERNANCE_VERDICT_META[verdict];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

// A workbench (agentic session) container's lifecycle, owned entirely
// server-side (S-15, AgentSessionStatus doc comment in types.ts). Mirrors
// ContainerStatePill's running/paused-esque tones: "pending" is a quiet
// startup wait (neutral/ring, not yet an in-progress action Comebye itself is
// taking), "running" is positive/dot, "stopped" is a deliberate terminal
// state (neutral/ring, not a problem), "failed" is negative/triangle.
const AGENT_SESSION_STATUS_META: Record<AgentSessionStatus, { label: string; tone: PillTone; shape: PillShape }> = {
  pending: { label: "Pending", tone: "neutral", shape: "ring" },
  running: { label: "Running", tone: "positive", shape: "dot" },
  stopped: { label: "Stopped", tone: "neutral", shape: "ring" },
  failed: { label: "Failed", tone: "negative", shape: "triangle" },
};

export function AgentSessionStatusPill({ status }: { status: AgentSessionStatus }) {
  const meta = AGENT_SESSION_STATUS_META[status];
  return <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />;
}

/**
 * A Bonnie/Develop session's worktree dirty/clean state (UX polish wave 3 —
 * folds the bespoke `.bonnie-dirty` pill CSS onto the shared `StatePill`
 * system, A-12). "Unknown" (never a fabricated "clean") covers a session
 * with a `branch` but no `dirty` report yet — the same "server says, client
 * renders honestly" posture as `ConnectionStatus` (S-15). "Uncommitted
 * changes" reads as warning/bars (something to look at before promoting),
 * "Clean" as positive/dot, "Unknown" as neutral/ring.
 */
export function WorktreeDirtyPill({ dirty }: { dirty: boolean | undefined }) {
  if (dirty === undefined) {
    return <StatePill label="Unknown" tone="neutral" shape="ring" />;
  }
  return dirty ? (
    <StatePill label="Uncommitted changes" tone="warning" shape="bars" />
  ) : (
    <StatePill label="Clean" tone="positive" shape="dot" />
  );
}
